'use client';

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

const serviceLinks = [
  { href: '/services/mixing-mastering', label: 'Mixing & Mastering' },
  { href: '/services/session-drumming', label: 'Session Drumming' },
  { href: '/services/post-production', label: 'Post Production' },
];

const navLinks: { href: string; label: string; dropdown?: boolean }[] = [
  { href: '/', label: 'Home' },
  { href: '/services', label: 'Services', dropdown: true },
  { href: '/shop', label: 'Shop' },
  { href: '/courses', label: 'Courses' },
  { href: '/music', label: 'Music' },
  { href: '/gallery', label: 'Gallery' },
  { href: '/about', label: 'About' },
  { href: '/contact', label: 'Contact' },
];

export default function Header() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Portal target only exists after mount (avoids SSR mismatch).
  useEffect(() => setMounted(true), []);

  // Compute the dropdown position from the trigger button. The header is
  // position:fixed so these viewport coordinates stay valid as the page
  // scrolls; we recompute on open and on resize.
  const positionDropdown = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setDropdownPos({ top: r.bottom + 8, left: r.left + r.width / 2 });
  };

  const openServices = () => {
    positionDropdown();
    setServicesOpen(true);
  };

  useEffect(() => {
    if (!servicesOpen) return;
    const onResize = () => positionDropdown();
    // Close when clicking outside both the trigger and the (portaled) menu.
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !dropdownRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setServicesOpen(false);
      }
    };
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [servicesOpen]);

  // Close the dropdown whenever the route changes.
  useEffect(() => setServicesOpen(false), [pathname]);

  const isServicesActive = serviceLinks.some((l) => pathname === l.href);

  return (
    <>
      <header
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 55,
          background: '#111111',
          borderBottom: '2px solid #ff1a1a',
        }}
      >
        <div
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
            padding: '0 24px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Logo */}
          <Link href="/" style={{ textDecoration: 'none' }}>
            <span
              className="logo-glitch"
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                fontSize: '1.3rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.8), 0 0 20px rgba(255,26,26,0.5)',
              }}
            >
              DIGGITY GLOVER
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) =>
              link.dropdown ? (
                /* Services Dropdown — trigger only; menu is portaled to body */
                <div key={link.href} style={{ position: 'relative' }} ref={dropdownRef}>
                  <button
                    ref={buttonRef}
                    className={`nav-link ${isServicesActive ? 'active' : ''}`}
                    onClick={() => (servicesOpen ? setServicesOpen(false) : openServices())}
                    onMouseEnter={openServices}
                  >
                    {link.label}
                  </button>

                  {/*
                    The menu renders into document.body via a portal so it
                    escapes the PageTransition stacking context. Otherwise the
                    fixed marquee banner (root z-index 45) would paint over it
                    during the page-fade animation regardless of its z-index.
                  */}
                  {mounted &&
                    servicesOpen &&
                    dropdownPos &&
                    createPortal(
                      <div
                        ref={menuRef}
                        style={{
                          position: 'fixed',
                          top: dropdownPos.top,
                          left: dropdownPos.left,
                          transform: 'translateX(-50%)',
                          width: '220px',
                          background: '#111111',
                          border: '1px solid #ff1a1a',
                          zIndex: 9999,
                        }}
                        onMouseLeave={() => setServicesOpen(false)}
                      >
                        {serviceLinks.map((sl) => (
                          <Link
                            key={sl.href}
                            href={sl.href}
                            style={{
                              display: 'block',
                              padding: '10px 16px',
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '0.75rem',
                              letterSpacing: '0.12em',
                              textTransform: 'uppercase',
                              color: pathname === sl.href ? '#ff1a1a' : '#cccccc',
                              textDecoration: 'none',
                              borderBottom: '1px solid rgba(255,26,26,0.2)',
                              transition: 'color 0.2s, background 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLElement).style.color = '#ff1a1a';
                              (e.currentTarget as HTMLElement).style.background =
                                'rgba(255,26,26,0.08)';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.color =
                                pathname === sl.href ? '#ff1a1a' : '#cccccc';
                              (e.currentTarget as HTMLElement).style.background = 'transparent';
                            }}
                            onClick={() => setServicesOpen(false)}
                          >
                            {sl.label}
                          </Link>
                        ))}
                      </div>,
                      document.body
                    )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`nav-link ${pathname === link.href ? 'active' : ''}`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              href="/contact"
              className="btn-primary"
              style={{ fontSize: '0.75rem', padding: '8px 18px' }}
            >
              Book Session
            </Link>
          </div>

          {/* Hamburger */}
          <button
            className="md:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '5px',
            }}
          >
            <span
              style={{
                display: 'block',
                width: '24px',
                height: '2px',
                background: '#ff1a1a',
                transition: 'all 0.3s',
                transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none',
              }}
            />
            <span
              style={{
                display: 'block',
                width: '24px',
                height: '2px',
                background: '#ff1a1a',
                transition: 'all 0.3s',
                opacity: menuOpen ? 0 : 1,
              }}
            />
            <span
              style={{
                display: 'block',
                width: '24px',
                height: '2px',
                background: '#ff1a1a',
                transition: 'all 0.3s',
                transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none',
              }}
            />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`mobile-menu ${menuOpen ? 'open' : ''}`}
        style={{
          position: 'fixed',
          top: '60px',
          left: 0,
          right: 0,
          zIndex: 40,
          background: '#000000',
          borderBottom: '1px solid #ff1a1a',
        }}
      >
        <nav
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
            padding: '16px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {navLinks.map((link) =>
            link.dropdown ? (
              /* Mobile Services */
              <div key={link.href} style={{ borderBottom: '1px solid rgba(255,26,26,0.2)' }}>
                <button
                  className={`nav-link ${isServicesActive ? 'active' : ''}`}
                  style={{ padding: '12px 0', width: '100%', textAlign: 'left', fontSize: '1rem' }}
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                >
                  {link.label} {mobileServicesOpen ? '▲' : '▼'}
                </button>
                {mobileServicesOpen && (
                  <div style={{ paddingLeft: '16px', paddingBottom: '8px' }}>
                    {serviceLinks.map((sl) => (
                      <Link
                        key={sl.href}
                        href={sl.href}
                        style={{
                          display: 'block',
                          padding: '8px 0',
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.75rem',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          color: pathname === sl.href ? '#ff1a1a' : '#cccccc',
                          textDecoration: 'none',
                        }}
                        onClick={() => {
                          setMenuOpen(false);
                          setMobileServicesOpen(false);
                        }}
                      >
                        {sl.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.href}
                href={link.href}
                className={`nav-link ${pathname === link.href ? 'active' : ''}`}
                style={{
                  padding: '12px 0',
                  borderBottom: '1px solid rgba(255,26,26,0.2)',
                  fontSize: '1rem',
                }}
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </Link>
            )
          )}

          <Link
            href="/contact"
            className="btn-primary"
            style={{ marginTop: '12px', textAlign: 'center' }}
            onClick={() => setMenuOpen(false)}
          >
            Book Session
          </Link>
        </nav>
      </div>
    </>
  );
}
